import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { IStorage, SortOrder, _AbcGridColumn, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { BasysMapFrameService, PanelComponent, RoutingService } from '@barthauer/basys/map-frame';
import { MapFrameSetting } from 'libs/basys/map-frame/src/lib/models/map-frame-settings.model';
import { MapFrameSettingsService } from 'libs/basys/map-frame/src/lib/services/map-frame-settings.service';
import { Observable } from 'rxjs';
import { AppService } from '../../services/app.service';
import {
  IMobileResourceType,
  MobileResourceEventGroupViewModel,
  MobileResourceEventTypeGroupViewModel,
  MobileResourceEventViewModel,
  MobileResourceViewModel,
} from '../../viewModels/mobileData.resources.viewModel';
import { ResourcesConstants, ResourcesDataContext, ResourcesNavigationContext } from '../resources.model';
import { ResourcesService } from '../resources.service';
import { ResourceListService } from './resource-list.service';
import { Format } from 'devextreme/common';

@Component({
  selector: 'app-resource-list',
  templateUrl: './resource-list.component.html',
  styleUrls: ['./resource-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResourceListComponent extends PanelComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('abcGridResourceEvent', { static: false }) abcGridResourceEvent: _AbcGridComponent;
  @ViewChild('filterDiv', { static: true }) filterDiv!: ElementRef<HTMLDivElement>;
  readonly VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT: number = 102;
  readonly VIEWPORTHEIGHTVERTICALMARGIN_IN_WORKMODE: number = 205;
  private _gridColumns: _AbcGridColumn[];
  public res$: Observable<ResourceSet>;
  public res: ResourceSet = ResourceSet.Empty;
  public AppService = AppService;
  public dataContext: ResourcesDataContext;
  public scrollViewPortElementName: string = 'resPanel';
  public isGridVisible: boolean;

  public get scrollViewPortHeightVerticalMargin(): number {
    return this.dataContext?.isInWorkMode
      ? this.VIEWPORTHEIGHTVERTICALMARGIN_IN_WORKMODE
      : this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
  }

  public get gridColumns(): _AbcGridColumn[] {
    if (!this._gridColumns) {
      this._gridColumns = this.createGridColumns();
    }
    return this._gridColumns;
  }

  public storage: Promise<IStorage>;

  constructor(
    private resManService: ResourceManagerService,
    public resourcesService: ResourcesService,
    public resourceListService: ResourceListService,
    frame: BasysMapFrameService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef,
    private settingsService: MapFrameSettingsService,
    private ngZone: NgZone,
  ) {
    super();
    super.init(frame);
    this.res$ = this.resManService.getResourceSetAsObservable('abc-maintenance-opera');
    this.storage = this.settingsService.createStorage(MapFrameSetting.gridLayouts);

    this.subscriptions.push(
      resourcesService.onDataContextSent().subscribe((context) => {
        this.dataContext = context;
        this.resourceListService.initResourceEventsDataSource(context);
        this.cdr.detectChanges();
      }),
    );
  }

  public getFilteredResources(): MobileResourceViewModel[] {
    return this.dataContext?.dsResources.filter((res) => res.resourceType.selected);
  }

  private createGridColumns(): _AbcGridColumn[] {
    return [
      {
        dataField: 'resourceType',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-RessourceType'),
        visible: false,
      },
      {
        dataField: 'code',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-Code'),
        visible: false,
      },
      {
        dataField: 'timespanOffAsHours',
        caption: this.res.get('Resources-ResourceEvent-PauseDuration'),
        format: ResourcesConstants.TimespanOffFormatString as Format,
        visible: false,
      },
      {
        dataField: 'value',
        caption: this.res.get('Resources-ResourceEvent-StartValue'),
        visible: false,
        sortOrder: SortOrder.Descending,
        calculateDisplayValue: (rowData) => this.calculateDisplayValue(rowData, 'value'),
      },
      {
        dataField: 'endValue',
        caption: this.res.get('Resources-ResourceEvent-EndValue'),
        visible: false,
        calculateDisplayValue: (rowData) => this.calculateDisplayValue(rowData, 'endValue'),
      },
      {
        dataField: 'name',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-Name'),
        visible: false,
      },
      {
        dataField: 'resourceName',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-Resource'),
      },
      {
        dataField: 'displayTime',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-Start'),
        dataType: 'datetime',
        format: _AbcGridComponent.getColumnFormat('datetime', undefined, true),
        calculateDisplayValue: (rowData) => this.calculateDisplayValue(rowData, 'displayTime'),
      },
      {
        dataField: 'displayEndTime',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-End'),
        dataType: 'datetime',
        format: _AbcGridComponent.getColumnFormat('datetime', undefined, true),
        calculateDisplayValue: (rowData) => this.calculateDisplayValue(rowData, 'displayEndTime'),
      },
      {
        dataField: 'resultValue',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-ResultValue'),
        dataType: 'number',
        calculateDisplayValue: (rowData) => this.calculateDisplayValue(rowData, 'resultValue'),
      },
      {
        dataField: 'resourceEventTypeKindName',
        caption: this.res.get('Resources-ResourceEvent-GridColumn-EventTypeKindName'),
      },
      {
        type: 'buttons',
        allowResizing: false,
        width: '55px',
        buttons: [{ icon: this.res.svgUrl('Edit-Image'), onClick: (e) => this.resourceEventGridRowClick(e.row.data) }],
      },
    ];
  }

  private calculateDisplayValue(rowData: any, fieldName: string): string | Date {
    let result: string | Date = undefined;
    if (rowData[fieldName]) {
      const evt: MobileResourceEventViewModel = rowData as MobileResourceEventViewModel;
      switch (fieldName) {
        case 'value':
        case 'endValue':
        case 'resultValue': {
          result = evt[fieldName].toLocaleString(undefined, {
            maximumFractionDigits: 1,
          });
          if (evt.unit) {
            result += ' ' + evt.unit;
          }
          break;
        }
        case 'displayTime': {
          result = evt.time
            ? evt.time
            : evt.date.toLocaleDateString(undefined, ResourcesConstants.SimpleDateFormatOptions);
          break;
        }
        case 'displayEndTime': {
          result = evt.endTime
            ? evt.endTime
            : evt.endDate.toLocaleDateString(undefined, ResourcesConstants.SimpleDateFormatOptions);
          break;
        }
      }
    }
    return result;
  }

  public resourceTypeChipClick(resType: IMobileResourceType): void {
    resType.selected = !resType.selected;
    if (this.isGridVisible) {
      this.abcGridResourceEvent.instance?.getDataSource()?.reload();
    }
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.res$.toPromise();
    super.updateSize(); // to ensure the size within work mode
  }

  ngAfterViewInit() {
    const currentRoute = this.routingService.getCurrentConfig().designation;
    if (currentRoute == AppService.Route_ResourceList) {
      this.ngZone.onStable.subscribe(() => {
        if (this.filterDiv?.nativeElement) {
          const height = this.filterDiv.nativeElement.getBoundingClientRect().height;

          const resCards = document.querySelectorAll('.res-card');
          if (resCards.length === 0) return;

          const lastCard = resCards[resCards.length - 1] as HTMLElement;

          if (height >= 200) {
            lastCard.style.marginBottom = '180px';
          } else if (height >= 160) {
            lastCard.style.marginBottom = '140px';
          } else if (height >= 120) {
            lastCard.style.marginBottom = '100px';
          } else if (height >= 80) {
            lastCard.style.marginBottom = '60px';
          } else {
            lastCard.style.marginBottom = '25px';
          }
        }
      });
    }
  }

  public toggleEventGridView(): void {
    this.isGridVisible = !this.isGridVisible;
  }

  public resourceCardActionClick(
    resource: MobileResourceViewModel,
    resourceEvtTypeGroup: MobileResourceEventTypeGroupViewModel,
  ): void {
    this.navigateToResourceDetail(undefined, resource, resourceEvtTypeGroup);
  }

  private resourceEventGridRowClick(resourceEvent: MobileResourceEventViewModel): void {
    this.navigateToResourceDetail(resourceEvent);
  }

  private navigateToResourceDetail(
    resourceEvent?: MobileResourceEventViewModel,
    resource?: MobileResourceViewModel,
    resourceEvtTypeGroup?: MobileResourceEventTypeGroupViewModel,
  ) {
    let isDirectSingleNavigation: boolean = false;
    if (resourceEvent) {
      isDirectSingleNavigation = true;
      resource = resourceEvent.resource;
      resourceEvtTypeGroup = resourceEvent.resourceEventTypeGroup;
    }
    const resourceEvtGroup: MobileResourceEventGroupViewModel = resource.resourceEventGroups.find(
      (resEvtGroup) => resEvtGroup.resourceEventTypeGroup === resourceEvtTypeGroup,
    );
    let resourceEventIndexInGroup: number = resourceEvtGroup.resourceEvents.length > 0 ? 0 : -1;
    if (resourceEvent) {
      resourceEventIndexInGroup = resourceEvtGroup.resourceEvents.indexOf(resourceEvent);
    }
    this.routingService.navigateName(resourceEvtTypeGroup.detailComponentRoute).then((succ) => {
      if (succ) {
        const context: ResourcesNavigationContext = new ResourcesNavigationContext();
        context.resourcesDataContext = this.dataContext;
        context.resourceEventGroup = resourceEvtGroup;
        context.resourceEventIndexInGroup = resourceEventIndexInGroup;
        context.isDirectSingleNavigation = isDirectSingleNavigation;
        this.dataContext.navigationContext = context;
        this.resourcesService.refreshNavigationContext(context);
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    //console.log('ResourceListComponent_ngOnDestroy');
  }
}
